import imageCompression from 'browser-image-compression';

/**
 * Image compression presets.
 *
 * The library runs inside a Web Worker so the main thread isn't blocked.
 * SVGs are passed through untouched (vector — no point compressing).
 *
 * `photo` is the default for product / hero images. `logo` is tighter for
 * brand logos where 1024px is plenty.
 */
export type CompressPreset = 'photo' | 'logo' | 'avatar';

const PRESETS: Record<CompressPreset, { maxSizeMB: number; maxWidthOrHeight: number }> = {
  photo: { maxSizeMB: 1, maxWidthOrHeight: 2048 },
  logo: { maxSizeMB: 0.3, maxWidthOrHeight: 1024 },
  avatar: { maxSizeMB: 0.2, maxWidthOrHeight: 512 },
};

/** Skip compression for SVG and tiny images already under threshold. */
function shouldSkip(file: File, preset: CompressPreset): boolean {
  if (file.type === 'image/svg+xml') return true;
  const maxBytes = PRESETS[preset].maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes * 0.5;
}

/**
 * Compress an image with the given preset. Returns the original file when
 * compression would not help (SVG or already small enough).
 *
 * If compression fails (e.g. unsupported codec on Safari), falls back to the
 * original file rather than blocking the upload.
 */
export async function compressImage(
  file: File,
  preset: CompressPreset = 'photo',
): Promise<File> {
  if (shouldSkip(file, preset)) return file;
  try {
    const options = {
      ...PRESETS[preset],
      useWebWorker: true,
      initialQuality: 0.85,
    };
    const out: Blob = await imageCompression(file, options);
    // The library returns a Blob — wrap it as a File so multipart upload keeps the original name.
    return out instanceof File ? out : new File([out], file.name, { type: out.type });
  } catch {
    return file;
  }
}
